import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  TextInput,
  PasswordInput,
  Paper,
  Title,
  Container,
  Button,
  Text,
  Alert,
  Stack,
  Box,
} from '@mantine/core';
import { useForm } from '@mantine/form';

export default function Login() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Email inválido'),
      password: (value) => (value.length < 6 ? 'La contraseña debe tener al menos 6 caracteres' : null),
    },
  });

  async function handleSubmit(values) {
    try {
      setError('');
      setLoading(true);
      await login(values.email, values.password);
      const returnTo = location.state?.homeServiceReturnTo;
      navigate(typeof returnTo === 'string' && /^\/solicitudes-a-domicilio(?:\/[a-zA-Z0-9]{20})?$/.test(returnTo) ? returnTo : '/');
    } catch (error) {
      setError('Error al iniciar sesión: ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box 
      sx={(theme) => ({
        minHeight: '100vh',
        background: 'radial-gradient(circle at top, rgba(99, 102, 241, 0.16), transparent 22%), radial-gradient(circle at 100% 0%, rgba(34, 211, 238, 0.14), transparent 22%), linear-gradient(180deg, #f8fafc 0%, #eef4fb 100%)',
        paddingTop: '8vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        paddingInline: theme.spacing.md,
      })}
    >
      <Container size={460} sx={{ paddingTop: 40, width: '100%' }}>
        <Stack align="center" spacing="xl" mb={50}>
          <Stack gap={8} align="center">
            <Text size="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.12em' }}>
              Club de la Barba
            </Text>
            <Title
            ta="center"
            sx={(theme) => ({
              fontFamily: theme.fontFamily,
              fontWeight: 900,
              fontSize: '2.2rem',
              letterSpacing: '-0.03em',
              background: theme.fn.linearGradient(45, theme.colors.indigo[7], theme.colors.cyan[5]),
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            })}
          >
            Giftcards y Códigos Club
            </Title>
            <Text c="dimmed" ta="center" maw={320}>
              Administra giftcards, ventas de la página y productos.
            </Text>
          </Stack>
        </Stack>

        <Paper
          withBorder
          shadow="lg"
          p={36}
          radius="xl"
          className="surface-panel"
          sx={(theme) => ({
            borderColor: 'rgba(226, 232, 240, 0.95)',
            boxShadow: theme.shadows.lg,
            backdropFilter: 'blur(10px)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(180deg, rgba(255,255,255,0.24), transparent 24%)',
              pointerEvents: 'none',
            },
          })}
        >
          <Stack gap={6} mb="xl" style={{ position: 'relative', zIndex: 1 }}>
            <Text fw={700} size="lg">Iniciar sesión</Text>
            <Text c="dimmed" size="sm">Sin cambios en el acceso, solo una interfaz más limpia.</Text>
          </Stack>

          {error && (
            <Alert 
              color="red" 
              mb="xl"
              title="Error"
              radius="md"
            >
              {error}
            </Alert>
          )}

          <form onSubmit={form.onSubmit(handleSubmit)} style={{ position: 'relative', zIndex: 1 }}>
            <Stack spacing="md">
              <TextInput
                required
                label="Correo Electrónico"
                placeholder="ejemplo@empresa.com"
                styles={(theme) => ({
                  input: {
                    backgroundColor: theme.white,
                    '&:focus': {
                      borderColor: theme.colors.indigo[5],
                    },
                  },
                })}
                {...form.getInputProps('email')}
              />

              <PasswordInput
                required
                label="Contraseña"
                placeholder="Tu contraseña"
                styles={(theme) => ({
                  input: {
                    backgroundColor: theme.white,
                    '&:focus': {
                      borderColor: theme.colors.indigo[5],
                    },
                  },
                })}
                {...form.getInputProps('password')}
              />

              <Button 
                type="submit" 
                loading={loading}
                fullWidth
                gradient={{ from: 'indigo', to: 'cyan' }}
                variant="gradient"
                sx={{
                  marginTop: '20px',
                  fontSize: '1rem',
                  height: '48px',
                }}
              >
                Iniciar Sesión
              </Button>
            </Stack>
          </form>
        </Paper>
      </Container>
    </Box>
  );
} 
